import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useEffect, useState } from "react";
import { TabsContent } from "@/components/ui/tabs";
import { SelectTimeZone } from "@/components/SelectTimeZone/SelectTimeZone";
import SearchSelectCompanies from "@/components/SearchSelectCompanies/SearchSelectCompanies";
import { SelectCommunityStatus } from "@/components/SelectCommunityStatus/SelectCommunityStatus";
import axios from '../../../lib/axios';
import { useParams } from "react-router-dom";
import { useToast } from "@/hooks/use-toast";
import { PhoneNumberInput } from "@/components/ui/phone-number-input";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { useIsUserAdmin } from "@/hooks/authHelpers";

export default function InfoTab() {
    const { id: communityId } = useParams();
    const { toast } = useToast();
    const [community, setCommunity] = useState({});
    const [initialCommunity, setInitialCommunity] = useState({});
    const [isLoading, setIsLoading] = useState(true);

    const isAdmin = useIsUserAdmin();

    const hasChanges = JSON.stringify(community) !== JSON.stringify(initialCommunity);

    const isSecretShopper = community?.status === 'secret-shopper';

    useEffect(() => {
        const fetchData = async () => {
            try {
                const response = await axios.get(`/communities/${communityId}`);
                setCommunity(response.data);
                setInitialCommunity(response.data);
            } catch (error) {
                console.error('Error fetching data:', error);
                toast({
                    title: "Error",
                    description: "Failed to load community details. Please try again.",
                    variant: "destructive",
                });
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();
    }, [communityId, toast]);


    const handleChange = (e) => {
        const { name, value } = e.target;
        setCommunity(prev => ({ ...prev, [name]: value }));
    };

    const handleTimeZoneChange = (timezone) => {
        setCommunity(prev => ({ ...prev, time_zone: timezone }))
    }

    const handleCompanyChange = (companies) => {
        setCommunity(prev => ({ ...prev, companies }))
    }

    const handleStatusChange = (status) => {
        setCommunity(prev => ({ ...prev, status }))
    }

    const handleSave = async (e) => {
        e.preventDefault();

        const data = { ...community };

        if (!data.website_url) {
            data.website_url = null;
        }

        setIsLoading(true)

        try {
            await axios.patch(`/communities/${communityId}`, {
                method: 'PATCH',
                headers: {
                    'Content-Type': 'application/json'
                },
                ...data,
                companies: community.companies?.map(({ id }) => id),
            });
            setInitialCommunity(community);
            toast({ title: "Success", description: "Community updated successfully." });
        } catch (error) {
            toast({
                title: "Error",
                description: "Failed to save changes. Please try again.",
                variant: "destructive",
            });
        } finally {
            setIsLoading(false);
        }
    };

    if (isLoading && !community?.id) {
        return (
            <TabsContent value="info">
                <div className="grid md:grid-cols-2 grid-cols-1 gap-6 mt-4">
                    {Array.from({ length: 7 }).map((_, i) => (
                        <div className="space-y-2" key={i}>
                            <Skeleton className="h-4 w-32" />
                            <Skeleton className="h-10 w-full rounded-md" />
                        </div>
                    ))}
                </div>
            </TabsContent>
        );
    }

    return (
        <TabsContent value="info">
            <form onSubmit={handleSave} className="grid md:grid-cols-2 grid-cols-1 gap-10 mt-4">
                <div>
                    <Label htmlFor="community_name">Community Name</Label>
                    <Input
                        disabled={!isAdmin && !isSecretShopper}
                        id="community_name"
                        name="community_name"
                        value={community.community_name}
                        onChange={handleChange}
                        required
                    />
                </div>
                <div>
                    <Label htmlFor="community_phone_number">Community Phone Number</Label>
                    <PhoneNumberInput
                        disabled={!isAdmin && !isSecretShopper}
                        id="community_phone_number"
                        name="community_phone_number"
                        value={community.community_phone_number}
                        onChange={handleChange}
                        required
                        pattern="^(\D*\d\D*){10}$"
                        title="Please enter a valid 10-digit phone number."
                    />
                </div>
                {!isSecretShopper && <div>
                    <Label htmlFor="assigned_flair_email">Assigned Flair Email</Label>
                    <Input
                        disabled={!isAdmin}
                        id="assigned_flair_email"
                        name="assigned_flair_email"
                        type="email"
                        value={community.assigned_flair_email}
                        onChange={handleChange}
                        required={!isSecretShopper}
                    />
                    <p className="text-sm text-gray-500">Auto-assigned by Flair</p>
                </div>}
                {!isSecretShopper && <div>
                    <Label htmlFor="assigned_flair_phone_number">Assigned Flair Phone Number</Label>
                    <PhoneNumberInput
                        disabled={!isAdmin}
                        id="assigned_flair_phone_number"
                        name="assigned_flair_phone_number"
                        value={community.assigned_flair_phone_number}
                        onChange={handleChange}
                        required={!isSecretShopper}
                        pattern="^(\D*\d\D*){10}$"
                        title="Please enter a valid 10-digit phone number."
                    />
                    <p className="text-sm text-gray-500">Auto-assigned by Flair</p>
                </div>}
                <div>
                    <Label htmlFor="community_phone_number_ext">Community Phone Number Extension</Label>
                    <Input
                        disabled={!isAdmin && !isSecretShopper}
                        id="community_phone_number_ext"
                        name="community_phone_number_ext"
                        value={community.community_phone_number_ext || ''}
                        onChange={handleChange}
                    />
                </div>
                {isAdmin && <div>
                    <Label htmlFor="monthly_price">Monthly Subscription Price</Label>
                    <Input
                        disabled={!isAdmin}
                        id="monthly_price"
                        name="monthly_price"
                        value={community.monthly_price || ''}
                        onChange={handleChange}
                    />
                </div>
                }
                <div>
                    <Label htmlFor="secondary_community_phone_number">Secondary Community Phone Number</Label>
                    <PhoneNumberInput
                        disabled={!isAdmin && !isSecretShopper}
                        id="secondary_community_phone_number"
                        name="secondary_community_phone_number"
                        value={community.secondary_community_phone_number}
                        onChange={handleChange}
                        pattern="^(\D*\d\D*){10}$"
                        title="Please enter a valid 10-digit phone number."
                    />
                </div>
                <div>
                    <Label htmlFor="time_zone">Time Zone</Label>
                    <SelectTimeZone
                        disabled={!isAdmin && !isSecretShopper}
                        id="time_zone"
                        name="time_zone"
                        className="w-full"
                        required
                        value={community.time_zone || ''}
                        onValueChange={handleTimeZoneChange}
                    />
                </div>
                <div>
                    <Label htmlFor="website_url">Website URL</Label>
                    <Input
                        disabled={!isAdmin && !isSecretShopper}
                        id="website_url"
                        name="website_url"
                        type="url"
                        placeholder="https://example.com"
                        value={community.website_url || ''}
                        onChange={handleChange}
                        required={isSecretShopper}
                    />
                </div>
                <div>
                    <Label htmlFor="status">Status</Label>
                    <SelectCommunityStatus
                        disabled={!isAdmin}
                        id="status"
                        name="status"
                        required
                        value={community?.status || ''}
                        onValueChange={handleStatusChange}
                    />
                </div>
                {isAdmin && <div>
                    <Label>Select Companies</Label>
                    <SearchSelectCompanies values={community.companies} onChange={handleCompanyChange} />
                </div>}
                <div className="md:col-span-2 flex justify-items-end">
                    <Button
                        isLoading={isLoading}
                        disabled={!hasChanges || (!isAdmin && !isSecretShopper)}
                        type="submit"
                    >
                        Save Changes
                    </Button>
                </div>
            </form>
        </TabsContent>
    );
}
